import {useState, useContext, useEffect} from 'react';
import getAPI from '../functions/API/getAPI.js'
import postAPI from '../functions/API/postAPI.js'
import { toast } from '../lib/toast.js';
import GlobalContext from "../context/GlobalContext.js";
import MFACode from '../components/MFACode.js';
import { darkenColor } from '../functions/colorUtils.js';
import {
  ShieldCheck,
  X,
  Mail,
} from 'lucide-react';


export default function ExtendedVerification() {

    const {settings, publicSettings} = useContext(GlobalContext)
    const [initialLoading, setInitialLoading] = useState(true)
    const [verificationMethods, setVerificationMethods] = useState();
    const [selectedContactMethod, setSelectedContactMethod] = useState({'id':"",'value':""});
    const [noMethods, setNoMethods] = useState(true);
    const [stage, setStage] = useState(1);
    const [loadingspinner, setLoadingSpinner] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);

    const primaryColor = settings?.primary_hex_colour || publicSettings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    function setParentCode(code) {
        completeVerification(code)
    }

    async function GetVerificationMethods() {
        const ExtendedVerificationResponse = await getAPI('getextendedverificationdetails');
        if (ExtendedVerificationResponse.data['contact details available']) {
            let length = ExtendedVerificationResponse.data['contact methods']['email addresses'].length;
            if (length > 0) {
                setNoMethods(false);
                setVerificationMethods(ExtendedVerificationResponse.data)
            }
        }
        setInitialLoading(false);
    }

    async function sendCode(){
        if (selectedContactMethod.id) {
            setSendingCode(true);
            const body = {id: selectedContactMethod.id}
            const VerificationCodeResponse = await postAPI('getextendedverificationcode', body);
            if(VerificationCodeResponse.response.ok){
                setStage(2)
            }else{
                toast.error('Sorry an error occured. Try again later.');
            }
            setSendingCode(false);
        } else {
            toast.error('Please select a contact method.');
        }
    }

    async function completeVerification(code){
        setLoadingSpinner(true);
        const body = {code: code}
        const CompleteVerificationResponse = await postAPI('completeextendedverification', body);
        if(CompleteVerificationResponse.response.ok){
            window.location.href = '/login'
        }else{
            toast.error('Sorry, your code was invalid. Please try again.');
            setLoadingSpinner(false)
        }
    }

    function back(){
        setStage(1);
    }

    useEffect(() => {
        GetVerificationMethods();
      }, []);

    const progressSteps = (
      <div className="flex gap-1.5 mb-8">
        <div className="flex-1 h-1 rounded-full bg-white" />
        <div className={`flex-1 h-1 rounded-full ${stage === 2 ? 'bg-white' : ''}`} style={stage === 1 ? { backgroundColor: 'rgba(255,255,255,0.25)' } : {}} />
        <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
      </div>
    );

    const gradientHeader = (className) => (
      <div className={className}>
        {stage === 2 && (
          <button
            onClick={back}
            className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
          >
            <X size={16} strokeWidth={2.5} />
            Back
          </button>
        )}
        {stage === 1 && <div className="mb-5" />}

        {progressSteps}

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <ShieldCheck size={22} className="text-white" strokeWidth={1.8} />
          </div>
          <h1 className="text-2xl font-bold text-white">Extended Verification</h1>
        </div>
      </div>
    );

    const formContent = (
      <>
        {initialLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
          </div>
        )}

        {noMethods && !initialLoading && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">Please call customer services</h2>
            <p className="text-sm text-gray-500">
              It would appear that we don't hold an email address for you. Please call customer services and an advisor will be able to update these records.
            </p>
          </div>
        )}

        {!noMethods && stage === 1 && !initialLoading && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">Select a contact method</h2>
            <p className="text-sm text-gray-500 mb-6">
              To complete your account setup, select a contact method below. We will send you a code to verify your identity.
            </p>
            <div className="space-y-3">
              {verificationMethods && (
                verificationMethods['contact methods']['email addresses'].map(function(email, index) {
                  return (
                    <button
                      key={index}
                      onClick={() => {setSelectedContactMethod({...selectedContactMethod, "id": email.id, 'value': email['email address']})}}
                      className={`w-full flex items-center gap-3 rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-gray-900 text-left transition-all ${
                        email.id === selectedContactMethod.id
                          ? 'shadow-sm'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={email.id === selectedContactMethod.id ? { borderColor: primaryColor } : {}}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={email.id === selectedContactMethod.id
                          ? { backgroundColor: primaryColor + '15', color: primaryColor }
                          : { backgroundColor: '#F3F4F6', color: '#6B7280' }
                        }
                      >
                        <Mail size={18} strokeWidth={1.8} />
                      </div>
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-gray-500 text-xs">{email['email address']}</p>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
            <button
              onClick={() => {sendCode()}}
              disabled={!selectedContactMethod.id || sendingCode}
              className="mt-6 w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              {sendingCode ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Send Verification Code'
              )}
            </button>
            <p className="mt-4 text-center text-sm text-gray-500">
              If you can't access the methods above, please call our customer services team.
            </p>
          </div>
        )}

        {!noMethods && stage === 2 && !initialLoading && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">Enter your verification code</h2>
            <p className="text-sm text-gray-500 mb-6">We've sent a verification code to your email. Enter it below to continue.</p>
            <MFACode setParentCode={setParentCode} loadingSpinner={loadingspinner} setLoadingSpinner={setLoadingSpinner} primaryColor={primaryColor}/>
            <p className="text-center text-sm text-gray-500 mt-6">
              Need to choose another method?{' '}
              <a href='#' onClick={(e)=>{e.preventDefault(); back()}} className="font-semibold hover:opacity-80" style={{ color: primaryColor }}>
                Go back
              </a>
            </p>
          </div>
        )}
      </>
    );

    return (
      <>
        {/* Mobile */}
        <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
          <div className="w-full flex-shrink-0" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
            {gradientHeader("px-5 pt-5 pb-12")}
          </div>
          <div className="flex-1 -mt-6 relative z-10">
            <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
              <div className="px-5 pt-7 pb-12">{formContent}</div>
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
          <div className="w-1/2 flex items-center justify-center p-12" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
            {gradientHeader("w-full max-w-md")}
          </div>
          <div className="w-1/2 flex items-center justify-center p-12">
            <div className="w-full max-w-md">{formContent}</div>
          </div>
        </div>
      </>
    );

}
